import type { z } from "zod";

import env from "utils/env";
import Sentry from "utils/sentry";

import { ReactError } from "./errors";

class SchemaError extends ReactError {
  declare extra: {
    input: unknown;
    issues: z.core.$ZodIssue[];
    schemaDescription?: string;
  };

  constructor(
    zodError: z.ZodError,
    schema: z.ZodType,
    input: unknown,
    componentName: string,
  ) {
    super(
      `Boundary schema validation of ${componentName} failed with ${schema.description ?? "unknown schema"}`,
      {
        schemaDescription: schema.description,
        issues: zodError.issues,
        input,
      },
    );

    this.name = "SchemaError";
  }

  get logMessage(): string {
    const issues = this.extra.issues
      .map((issue) => {
        const path = issue.path.join(".") || "(root)";
        return `\t • ${path}: ${issue.message} (${issue.code})`;
      })
      .join("\n");

    return `${this.message}\n${issues}`;
  }
}

export default function validateSchema(
  schema: z.ZodType,
  input: unknown,
  componentName: string,
): boolean {
  const { success, error: zodError } = schema.safeParse(input);

  if (!success) {
    const error = new SchemaError(zodError, schema, input, componentName);
    Sentry.captureException(error);

    if (env.REACT_BOUNDARY_VALIDATION_LOGGING === "on") {
      // eslint-disable-next-line no-console
      console.error(error.logMessage);
    }
  }

  return success;
}
